// Render the OwnerRez quote as a guest-facing price card, not a ledger. The
// API's charge lines arrive with accounting descriptions ("Direct Booking
// Discount based on 3% per stay (rent only)", five separate named tax lines);
// we normalize them to the short labels a booking widget uses, fold the taxes
// into one line with an expandable breakdown, and lead with the per-night
// price guests compare homes by — struck-through base rate next to the direct
// rate when a discount applies. Nothing is hidden, just organized.
//
// The v2 quote returns line items in a `charges` collection (each
// { description, amount, type }); older/other shapes expose `rent` +
// `surcharges` + `taxes` + a top-level `total`. We handle both, and fall back
// to summing lines when no explicit total is present.

import { Eyebrow } from '../Section.jsx';
import { DIRECT_DISCOUNT_PCT } from '../../lib/property-schema.js';

function fmt(amount, currency = 'USD', maxDigits = null) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    ...(maxDigits != null ? { minimumFractionDigits: 0, maximumFractionDigits: maxDigits } : {})
  }).format(Number(amount) || 0);
}

function rawLines(quote) {
  if (Array.isArray(quote.charges) && quote.charges.length) {
    return quote.charges.map((c) => ({
      label: c.description || c.type || 'Charge',
      amount: Number(c.amount) || 0,
      type: c.type || ''
    }));
  }
  // Fallback: the older rent/surcharges/taxes shape.
  const items = [];
  const rent = quote.rent ?? quote.subtotal;
  if (rent != null) items.push({ label: 'Nightly rent', amount: Number(rent) || 0, type: 'rent' });
  (quote.surcharges || []).forEach((s) =>
    items.push({ label: s.description || s.name || 'Fee', amount: Number(s.amount) || 0, type: 'surcharge' })
  );
  (quote.taxes || []).forEach((t) =>
    items.push({ label: t.description || t.name || 'Tax', amount: Number(t.amount) || 0, type: 'tax' })
  );
  return items;
}

function parseQuote(quote) {
  const lines = [];
  const taxes = [];
  let rent = null; // { amount, nights }
  let discountTotal = 0;

  for (const item of rawLines(quote)) {
    if (/tax/i.test(item.type) || /\btax\b/i.test(item.label)) {
      taxes.push(item);
      continue;
    }
    const nights = /^(\d+)\s+nights?\b/i.exec(item.label);
    if (nights && item.amount > 0 && !rent) {
      rent = { amount: item.amount, nights: Number(nights[1]) };
      continue;
    }
    if (item.amount < 0) {
      discountTotal += item.amount;
      const pct = /(\d+(?:\.\d+)?)\s*%/.exec(item.label);
      lines.push({
        label: /discount/i.test(item.label)
          ? `Direct discount${pct ? ` (${pct[1]}%)` : ''}`
          : item.label,
        amount: item.amount,
        discount: true
      });
      continue;
    }
    if (/cleaning/i.test(item.label)) {
      lines.push({ label: 'Cleaning fee', amount: item.amount });
      continue;
    }
    lines.push({ label: item.label, amount: item.amount });
  }
  return { rent, lines, taxes, discountTotal };
}

// The OwnerRez tax names are registration names ("Charleston County Local
// Sales & Use Tax") and wrap three lines each in the card's column. Shorten
// them for display; the checkout page still shows the registered names.
// Charleston-market specific — Phase 2 (multi-market) moves this per-market.
function shortTaxLabel(label) {
  return label
    .replace(/south carolina/i, 'SC')
    .replace(/charleston county local/i, 'County')
    .replace(/town of mount pleasant local/i, 'Town');
}

function Card({ children }) {
  return <div className="rounded-xl border border-navy-100 bg-cream-50 p-5 text-sm">{children}</div>;
}

// `comparison` is the server's estimate of the same stay on Airbnb (see
// src/lib/direct-savings.js), shown under the total so the dollar gap sits
// where the booking decision happens. Taxes included on both sides (owner
// call 2026-09-20), so the direct figure equals the Total above it; labeled
// an estimate, with its assumptions folded behind a one-line trigger. Absent,
// the card falls back to the plain "saving $X with the direct rate" sentence.
export default function QuoteSummary({ quote, loading, error, comparison }) {
  // Branch order is deliberate. Stay.jsx keeps the last quote in state while
  // a refetch runs (a guest-count or code change; a date tap clears it) and
  // does not clear it when that refetch fails, so an error must win over the
  // stale quote. With a quote on hand, a refetch dims the priced card in
  // place; the one-line "Calculating" card only serves a fetch with nothing
  // to show yet, because swapping it in for the priced card shrank the rail
  // by a few hundred pixels and moved the page on every guest-count change.
  if (error) {
    // The message is already guest-facing (rule violations arrive as full
    // sentences from the quote endpoint; everything else is its generic copy).
    // charlestonpink-600 is the brand's alert color.
    return (
      <Card>
        <Eyebrow text="Your quote" />
        <p className="text-charlestonpink-600">{error}</p>
      </Card>
    );
  }
  if (loading && !quote) {
    return (
      <Card>
        <Eyebrow text="Your quote" />
        <p className="text-navy-500">Calculating your quote…</p>
      </Card>
    );
  }
  if (!quote) {
    return (
      <Card>
        <Eyebrow text="Your quote" />
        <p className="text-navy-600">
          Pick check-in and check-out dates to see your price. Direct quotes take {DIRECT_DISCOUNT_PCT}% off
          nightly rates automatically, no code needed.
        </p>
      </Card>
    );
  }

  const { rent, lines, taxes, discountTotal } = parseQuote(quote);
  const taxTotal = taxes.reduce((a, t) => a + t.amount, 0);
  const summed = (rent?.amount || 0) + lines.reduce((a, l) => a + l.amount, 0) + taxTotal;
  const total = quote.total ?? quote.total_amount ?? quote.amount ?? summed;
  const currency = quote.currency_code || quote.currency || 'USD';

  const nightlyBase = rent && rent.nights > 0 ? rent.amount / rent.nights : null;
  const nightlyNet = nightlyBase != null ? (rent.amount + discountTotal) / rent.nights : null;
  const hasDiscount = discountTotal < 0;

  const row = (label, amount, extra = '', key = undefined) => (
    <div key={key} className={`flex items-baseline justify-between gap-3${extra}`}>
      <span>{label}</span>
      <span className="shrink-0 tabular-nums">{fmt(amount, currency)}</span>
    </div>
  );

  return (
    <Card>
      <div className="flex items-baseline gap-3">
        <div className="min-w-0 flex-1">
          <Eyebrow text="Your quote" />
        </div>
        {loading && <p className="text-xs italic text-navy-500">Updating…</p>}
      </div>

      {/* Dimmed, not replaced, while a refetch runs: the kicker row above keeps
          its height either way, so nothing below the card moves. */}
      <div aria-busy={loading} className={`transition-opacity${loading ? ' opacity-60' : ''}`}>
        {nightlyNet != null && (
          <div className="flex flex-wrap items-baseline gap-x-2">
            {hasDiscount && (
              // navy-500, not 400: the struck rate at 16px measured 3.79:1 on
              // cream; 500 is 5.69:1 and still reads as the crossed-out number.
              <span className="text-base text-navy-500 line-through">
                {fmt(nightlyBase, currency, 0)}
              </span>
            )}
            <span className="font-display text-3xl font-semibold leading-none text-navy-900">
              {fmt(nightlyNet, currency, 0)}
            </span>
            <span className="text-navy-600">a night</span>
          </div>
        )}

        <div className="mt-4 space-y-2 border-t border-navy-100 pt-4 text-navy-700">
          {rent && row(`${rent.nights} night${rent.nights === 1 ? '' : 's'}`, rent.amount)}
          {lines.map((l, i) => row(l.label, l.amount, l.discount ? ' font-medium text-marsh-700' : '', i))}
          {taxes.length === 1 && row(taxes[0].label, taxTotal)}
          {taxes.length > 1 && (
            <details className="group">
              <summary className="flex cursor-pointer select-none items-baseline justify-between gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-700 [&::-webkit-details-marker]:hidden">
                <span>
                  Taxes
                  <span className="ml-1.5 text-xs text-cream-600 underline decoration-dotted underline-offset-2 group-open:hidden">
                    breakdown
                  </span>
                </span>
                <span className="shrink-0 tabular-nums">{fmt(taxTotal, currency)}</span>
              </summary>
              <div className="mt-1.5 space-y-1 border-l-2 border-cream-200 pl-3 text-xs text-navy-500">
                {taxes.map((t, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-3">
                    <span>{shortTaxLabel(t.label)}</span>
                    <span className="shrink-0 tabular-nums">{fmt(t.amount, currency)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <div className="mt-4 flex items-baseline justify-between border-t border-navy-100 pt-3">
          <span className="font-display text-base font-semibold text-navy-900">Total</span>
          <span className="font-display text-xl font-semibold tabular-nums text-navy-900">
            {fmt(total, currency)}
          </span>
        </div>
        {comparison && comparison.saving > 0 ? (
          <div className="mt-3 border-t border-navy-100 pt-3 text-navy-700">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-cream-600">
              Same stay, taxes included
            </p>
            <div className="mt-1.5 space-y-1.5">
              {row('On Airbnb, estimated', comparison.airbnb.total)}
              {row('Booking direct', comparison.direct.total)}
            </div>
            <p className="mt-2 text-sm font-medium text-marsh-700">
              About {fmt(comparison.saving, currency, 0)} less booking direct
              {comparison.savingPct > 0 ? ` (${comparison.savingPct}%)` : ''}.
            </p>
            {/* The assumption is the server's sourced statement and stays
                verbatim; folded so the saving, not the footnote, is the last
                thing read before Book direct. */}
            <details className="mt-1.5">
              <summary className="inline-block cursor-pointer select-none text-xs text-cream-600 underline decoration-dotted underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-700 [&::-webkit-details-marker]:hidden">
                How we estimate the Airbnb figure
              </summary>
              <p className="mt-1.5 text-xs leading-relaxed text-navy-500">{comparison.assumption}</p>
            </details>
          </div>
        ) : (
          <>
            {hasDiscount && (
              <p className="mt-2 text-xs font-medium text-marsh-700">
                You're saving {fmt(-discountTotal, currency)} with the direct rate.
              </p>
            )}
            {/* Only without the comparison block: next to it, this line repeated
                the block's own kicker. */}
            <p className="mt-2 text-xs text-navy-500">
              Taxes included, no service fee. Same calendar as Airbnb and Vrbo.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}
